/**
 * Button Atom - Standardized button component
 *
 * This is a basic UI element following atomic design principles.
 * It extends Chakra UI's button with loading states and consistent API.
 */

import type { ButtonProps as ChakraButtonProps } from "@chakra-ui/react"
import {
  AbsoluteCenter,
  Button as ChakraButton,
  Span,
  Spinner,
} from "@chakra-ui/react"
import * as React from "react"

interface ButtonLoadingProps {
  loading?: boolean
  loadingText?: React.ReactNode
}

export interface ButtonProps extends ChakraButtonProps, ButtonLoadingProps {
  // Required props (none for button)

  // Optional props with sensible defaults
  variant?: "solid" | "outline" | "ghost" | "subtle" | "surface" | "plain"
  size?: "xs" | "sm" | "md" | "lg"
  colorScheme?: string

  // Event handlers
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void

  // Accessibility
  "aria-label"?: string
  "aria-describedby"?: string

  // Styling escape hatch
  className?: string
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(props, ref) {
    const {
      loading,
      disabled,
      loadingText,
      children,
      variant = "solid",
      size = "md",
      ...rest
    } = props

    return (
      <ChakraButton
        disabled={loading || disabled}
        variant={variant}
        size={size}
        ref={ref}
        {...rest}
      >
        {loading && !loadingText ? (
          <>
            <AbsoluteCenter display="inline-flex">
              <Spinner size="inherit" color="inherit" />
            </AbsoluteCenter>
            <Span opacity={0}>{children}</Span>
          </>
        ) : loading && loadingText ? (
          <>
            <Spinner size="inherit" color="inherit" />
            {loadingText}
          </>
        ) : (
          children
        )}
      </ChakraButton>
    )
  },
)

Button.displayName = "Button"

export default Button
